import { randomBetween } from '../helpers';
import { createSnowSprites } from './snowSprites';

const FULL_TURN = Math.PI * 2;

const MAX_FRAME_SECONDS = 0.05;

const MAX_PIXEL_RATIO = 2;

const DENSITY = { flakesPerMegapixel: 115, minFlakes: 40 };

const FLAKE = {
  fallSpeed: 280,
  nearSize: 14,
  farProximity: 0.12,
  proximitySkew: 2.2,
  clumpProximity: 0.4,
  sizeVariation: { min: 0.8, max: 1.25 },
  fallVariation: { min: 0.85, max: 1.15 },
  farOpacity: 0.4,
  nearOpacityGain: 0.55,
  opacityJitter: { min: -0.08, max: 0.08 },
  windResponse: { min: 0.7, max: 1.8 },
  turbulenceBase: 0.03,
  turbulenceRange: 0.2,
  turbulenceSkew: 2.5,
  driftPeriod: { min: 2.4, max: 6.5 },
  flutterPeriod: { min: 0.8, max: 1.6 },
  driftWeight: 0.7,
  flutterWeight: 0.3,
  respawnSpread: 40,
};

const BLURRED_FLAKE = {
  share: 0.02,
  proximity: { min: 1.4, max: 2 },
  size: { min: 18, max: 28 },
  opacity: { min: 0.18, max: 0.3 },
};

const WIND = {
  mean: 0.12,
  waves: [
    { amplitude: 0.09, frequency: 0.11, phase: 1.3 },
    { amplitude: 0.05, frequency: 0.27, phase: 0.4 },
    { amplitude: 0.03, frequency: 0.63, phase: 2.1 },
  ],
  gustFrontSpeed: 1100,
};

const GUST = {
  firstAt: 5,
  interval: { min: 16, max: 32 },
  rise: { min: 1.8, max: 2.6 },
  hold: { min: 0.6, max: 1.6 },
  fall: { min: 3.5, max: 5.5 },
  strength: { min: 0.22, max: 0.4 },
};

const LONGEST_GUST = GUST.rise.max + GUST.hold.max + GUST.fall.max;

interface Flake {
  sprite: HTMLCanvasElement;
  proximity: number;
  size: number;
  fallSpeed: number;
  opacity: number;
  windResponse: number;
  turbulence: number;
  driftFrequency: number;
  driftPhase: number;
  flutterFrequency: number;
  flutterPhase: number;
  x: number;
  y: number;
  velocityX: number;
}

interface Gust {
  start: number;
  rise: number;
  hold: number;
  fall: number;
  strength: number;
}

interface GustStrengthArgs {
  gust: Gust;
  time: number;
}

interface PickSpriteArgs {
  isBlurred: boolean;
  proximity: number;
}

interface MoveFlakeArgs {
  flake: Flake;
  elapsed: number;
}

const smoothstep = (value: number) => {
  if (value <= 0) {
    return 0;
  }

  if (value >= 1) {
    return 1;
  }

  return value * value * (3 - 2 * value);
};

const getGustStrength = ({ gust, time }: GustStrengthArgs) => {
  const { start, rise, hold, fall, strength } = gust;
  const age = time - start;

  if (age <= 0 || age >= rise + hold + fall) {
    return 0;
  }

  if (age < rise) {
    return strength * smoothstep(age / rise);
  }

  if (age < rise + hold) {
    return strength;
  }

  return strength * (1 - smoothstep((age - rise - hold) / fall));
};

const getBaseWind = (time: number) =>
  WIND.waves.reduce(
    (sum, { amplitude, frequency, phase }) => sum + amplitude * Math.sin(time * frequency + phase),
    WIND.mean
  );

export const startSnowfall = (canvas: HTMLCanvasElement) => {
  const context = canvas.getContext('2d');

  if (!context) {
    return undefined;
  }

  const sprites = createSnowSprites();
  const gusts: Gust[] = [];
  const flakes: Flake[] = [];
  let width = 0;
  let height = 0;
  let pixelRatio = 1;
  let time = 0;
  let lastFrame = performance.now();
  let frameId = 0;
  let nextGustAt = GUST.firstAt;

  const getWind = (x: number) => {
    const windTime = time - x / WIND.gustFrontSpeed;

    return gusts.reduce(
      (sum, gust) => sum + getGustStrength({ gust, time: windTime }),
      getBaseWind(windTime)
    );
  };

  const addGust = () => {
    gusts.push({
      start: time,
      rise: randomBetween(GUST.rise),
      hold: randomBetween(GUST.hold),
      fall: randomBetween(GUST.fall),
      strength: randomBetween(GUST.strength),
    });
    nextGustAt = time + randomBetween(GUST.interval);
  };

  const pickSprite = ({ isBlurred, proximity }: PickSpriteArgs) => {
    if (isBlurred) {
      return sprites.blur;
    }

    if (proximity > FLAKE.clumpProximity) {
      return sprites.clumps[Math.floor(Math.random() * sprites.clumps.length)];
    }

    return sprites.speck;
  };

  const createFlake = (): Flake => {
    const isBlurred = Math.random() < BLURRED_FLAKE.share;
    const proximity = isBlurred
      ? randomBetween(BLURRED_FLAKE.proximity)
      : FLAKE.farProximity + (1 - FLAKE.farProximity) * Math.random() ** FLAKE.proximitySkew;
    const nearness = (proximity - FLAKE.farProximity) / (1 - FLAKE.farProximity);
    const size = isBlurred
      ? randomBetween(BLURRED_FLAKE.size)
      : FLAKE.nearSize * proximity * randomBetween(FLAKE.sizeVariation);
    const fallSpeed = FLAKE.fallSpeed * proximity * randomBetween(FLAKE.fallVariation);
    const x = Math.random() * width;

    return {
      sprite: pickSprite({ isBlurred, proximity }),
      proximity,
      size,
      fallSpeed,
      opacity: isBlurred
        ? randomBetween(BLURRED_FLAKE.opacity)
        : Math.min(
            1,
            FLAKE.farOpacity + FLAKE.nearOpacityGain * nearness + randomBetween(FLAKE.opacityJitter)
          ),
      windResponse: randomBetween(FLAKE.windResponse),
      turbulence:
        fallSpeed *
        (FLAKE.turbulenceBase + FLAKE.turbulenceRange * Math.random() ** FLAKE.turbulenceSkew),
      driftFrequency: FULL_TURN / randomBetween(FLAKE.driftPeriod),
      driftPhase: Math.random() * FULL_TURN,
      flutterFrequency: FULL_TURN / randomBetween(FLAKE.flutterPeriod),
      flutterPhase: Math.random() * FULL_TURN,
      x,
      y: randomBetween({ min: -size, max: height + size }),
      velocityX: getWind(x) * fallSpeed,
    };
  };

  const respawnFlake = (flake: Flake) => {
    flake.x = Math.random() * width;
    flake.y = -flake.size - Math.random() * FLAKE.respawnSpread;
    flake.velocityX = getWind(flake.x) * flake.fallSpeed;
  };

  const moveFlake = ({ flake, elapsed }: MoveFlakeArgs) => {
    const {
      fallSpeed,
      turbulence,
      windResponse,
      size,
      driftFrequency,
      driftPhase,
      flutterFrequency,
      flutterPhase,
    } = flake;
    const swirl =
      FLAKE.driftWeight * Math.sin(time * driftFrequency + driftPhase) +
      FLAKE.flutterWeight * Math.sin(time * flutterFrequency + flutterPhase);
    const targetVelocityX = getWind(flake.x) * fallSpeed + turbulence * swirl;

    flake.velocityX += (targetVelocityX - flake.velocityX) * Math.min(1, elapsed * windResponse);
    flake.x += flake.velocityX * elapsed;
    flake.y += fallSpeed * elapsed;

    if (flake.y - size > height) {
      respawnFlake(flake);
    }

    if (flake.x > width + size) {
      flake.x -= width + size * 2;
    } else if (flake.x < -size) {
      flake.x += width + size * 2;
    }
  };

  const step = (now: number) => {
    const elapsed = Math.min((now - lastFrame) / 1000, MAX_FRAME_SECONDS);

    lastFrame = now;
    time += elapsed;

    if (time >= nextGustAt) {
      addGust();
    }

    while (gusts.length && time - gusts[0].start > LONGEST_GUST + width / WIND.gustFrontSpeed) {
      gusts.shift();
    }

    context.setTransform(1, 0, 0, 1, 0, 0);
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    flakes.forEach((flake) => {
      moveFlake({ flake, elapsed });

      const { sprite, opacity, x, y, size } = flake;

      context.globalAlpha = opacity;
      context.drawImage(sprite, x - size / 2, y - size / 2, size, size);
    });
    frameId = requestAnimationFrame(step);
  };

  const resize = () => {
    pixelRatio = Math.min(window.devicePixelRatio || 1, MAX_PIXEL_RATIO);
    width = canvas.clientWidth;
    height = canvas.clientHeight;
    canvas.width = Math.round(width * pixelRatio);
    canvas.height = Math.round(height * pixelRatio);

    const count = Math.max(
      DENSITY.minFlakes,
      Math.round(((width * height) / 1e6) * DENSITY.flakesPerMegapixel)
    );

    while (flakes.length > count) {
      flakes.splice(Math.floor(Math.random() * flakes.length), 1);
    }

    if (flakes.length < count) {
      flakes.push(...Array.from({ length: count - flakes.length }, createFlake));
      flakes.sort(({ proximity: first }, { proximity: second }) => first - second);
    }
  };

  resize();
  window.addEventListener('resize', resize);
  frameId = requestAnimationFrame(step);

  return () => {
    cancelAnimationFrame(frameId);
    window.removeEventListener('resize', resize);
  };
};
